import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, downloadCsv, get, post, qs } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { SchoolDisplayDialog } from '../components/SchoolDisplayDialog.js';
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  Failure,
  Field,
  Loading,
  Modal,
  Notice,
  Pagination,
} from '../components/ui.js';

interface School {
  id: string;
  name: string;
  code: string;
  timezone: string;
  address: string | null;
  contact: string | null;
  device_count: number;
}

interface Group {
  id: string;
  name: string;
  priority: number;
  school_count: number;
  school_ids: string[];
  template_id: string | null;
  template_name: string | null;
}

interface Page<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export function SchoolsPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showSchoolForm, setShowSchoolForm] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [deleteGroup, setDeleteGroup] = useState<Group | null>(null);
  const [displaySchool, setDisplaySchool] = useState<School | null>(null);
  const [error, setError] = useState<string | null>(null);

  const schools = useQuery({
    queryKey: ['schools', page, search],
    queryFn: () => get<Page<School>>(`/schools${qs({ page, page_size: 25, q: search })}`),
  });

  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () => get<Page<Group>>('/groups?page_size=100'),
  });

  const createSchool = useMutation({
    mutationFn: (body: Record<string, string>) => post('/schools', body),
    onSuccess: () => {
      setShowSchoolForm(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const createGroup = useMutation({
    mutationFn: (body: { name: string; priority: number; school_ids: string[] }) =>
      post('/groups', body),
    onSuccess: () => {
      setShowGroupForm(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const removeGroup = useMutation({
    mutationFn: (id: string) => del(`/groups/${id}`),
    onSuccess: () => {
      setDeleteGroup(null);
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  // Two groups at the same priority assigning different templates block publishing (§8).
  const priorityClashes = (() => {
    const byPriority = new Map<number, Group[]>();
    for (const g of groups.data?.items ?? []) {
      if (!g.template_id) continue;
      byPriority.set(g.priority, [...(byPriority.get(g.priority) ?? []), g]);
    }
    return [...byPriority.entries()].filter(
      ([, list]) => new Set(list.map((g) => g.template_id)).size > 1,
    );
  })();

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      {priorityClashes.length > 0 ? (
        <Notice tone="danger">
          تعارض أولويات يمنع النشر:{' '}
          {priorityClashes
            .map(([p, list]) => `الأولوية ${p} (${list.map((g) => g.name).join('، ')})`)
            .join(' — ')}
          . غيّر أولوية إحدى المجموعات أو وحّد القالب.
        </Notice>
      ) : null}

      <Card
        title="المدارس"
        subtitle={`${schools.data?.total ?? 0} مدرسة`}
        actions={
          <>
            <input
              placeholder="بحث بالاسم أو الرمز"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{ width: 220 }}
            />
            <button onClick={() => void downloadCsv('/schools?format=csv', 'schools.csv')}>
              تصدير CSV
            </button>
            {can('school.manage') ? (
              <button className="primary" onClick={() => setShowSchoolForm(true)}>
                إضافة مدرسة
              </button>
            ) : null}
          </>
        }
      >
        {schools.isLoading ? (
          <Loading />
        ) : schools.error ? (
          <Failure error={schools.error} onRetry={() => void schools.refetch()} />
        ) : schools.data!.items.length === 0 ? (
          <Empty
            title="لا توجد مدارس"
            hint={search ? 'لا نتائج مطابقة للبحث.' : 'ابدأ بإضافة مدرسة ثم أجهزتها.'}
          />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الاسم</th>
                    <th>الرمز</th>
                    <th>المنطقة الزمنية</th>
                    <th>الأجهزة</th>
                    <th>العنوان</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {schools.data!.items.map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td className="mono">{s.code}</td>
                      <td className="mono">{s.timezone}</td>
                      <td>
                        <Link to={`/devices${qs({ school_id: s.id })}`}>
                          <span className="num">{s.device_count}</span> جهاز
                        </Link>
                      </td>
                      <td style={{ color: 'var(--muted)' }}>{s.address ?? '—'}</td>
                      <td className="actions">
                        {can('school.manage') ? (
                          <button className="ghost" onClick={() => setDisplaySchool(s)}>
                            شاشة المدرسة
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={schools.data!.page}
              totalPages={schools.data!.total_pages}
              total={schools.data!.total}
              onChange={setPage}
            />
          </>
        )}
      </Card>

      <Card
        title="مجموعات المدارس"
        subtitle="الأولوية الأعلى رقمًا تفوز عند إسناد الجداول"
        actions={
          can('group.manage') ? (
            <button className="primary" onClick={() => setShowGroupForm(true)}>
              إنشاء مجموعة
            </button>
          ) : null
        }
      >
        {groups.isLoading ? (
          <Loading />
        ) : groups.error ? (
          <Failure error={groups.error} onRetry={() => void groups.refetch()} />
        ) : groups.data!.items.length === 0 ? (
          <Empty title="لا توجد مجموعات" hint="المجموعات تسهّل إسناد جدول واحد لعدة مدارس." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>المجموعة</th>
                  <th>الأولوية</th>
                  <th>المدارس</th>
                  <th>القالب المسند</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {groups.data!.items.map((g) => (
                  <tr key={g.id}>
                    <td>{g.name}</td>
                    <td className="num">{g.priority}</td>
                    <td className="num">{g.school_count}</td>
                    <td>
                      {g.template_name ? (
                        <Badge tone="info" icon="◷">
                          {g.template_name}
                        </Badge>
                      ) : (
                        <span style={{ color: 'var(--muted)' }}>—</span>
                      )}
                    </td>
                    <td className="actions">
                      {can('group.manage') ? (
                        <button className="ghost" onClick={() => setDeleteGroup(g)}>
                          حذف
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showSchoolForm ? (
        <SchoolForm
          busy={createSchool.isPending}
          onCancel={() => setShowSchoolForm(false)}
          onSubmit={(body) => createSchool.mutate(body)}
        />
      ) : null}

      {showGroupForm ? (
        <GroupForm
          schools={schools.data?.items ?? []}
          busy={createGroup.isPending}
          onCancel={() => setShowGroupForm(false)}
          onSubmit={(body) => createGroup.mutate(body)}
        />
      ) : null}

      {displaySchool ? (
        <SchoolDisplayDialog
          schoolId={displaySchool.id}
          schoolName={displaySchool.name}
          onClose={() => setDisplaySchool(null)}
        />
      ) : null}

      {deleteGroup ? (
        <ConfirmDialog
          title="حذف المجموعة"
          danger
          busy={removeGroup.isPending}
          message={
            <>
              سيُحذف تجميع «{deleteGroup.name}» وإسناد قالبه. لن تُحذف المدارس نفسها، ولن يتغير أي
              جدول منشور حتى تعيد النشر.
            </>
          }
          confirmLabel="حذف"
          onCancel={() => setDeleteGroup(null)}
          onConfirm={() => removeGroup.mutate(deleteGroup.id)}
        />
      ) : null}
    </>
  );
}

function SchoolForm({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, string>) => void;
}): JSX.Element {
  const [form, setForm] = useState({
    name: '',
    code: '',
    timezone: 'Asia/Baghdad',
    address: '',
    contact: '',
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      title="إضافة مدرسة"
      onClose={onCancel}
      footer={
        <>
          <button onClick={onCancel} disabled={busy}>
            إلغاء
          </button>
          <button
            className="primary"
            disabled={busy || !form.name || !form.code}
            onClick={() =>
              onSubmit({
                name: form.name.trim(),
                code: form.code.trim(),
                timezone: form.timezone.trim(),
                ...(form.address ? { address: form.address.trim() } : {}),
                ...(form.contact ? { contact: form.contact.trim() } : {}),
              })
            }
          >
            {busy ? 'جارٍ الحفظ…' : 'حفظ'}
          </button>
        </>
      }
    >
      <Field label="اسم المدرسة">
        <input value={form.name} onChange={set('name')} required />
      </Field>
      <Field label="رمز المدرسة" hint="حروف لاتينية وأرقام وشرطات، فريد داخل المديرية">
        <input value={form.code} onChange={set('code')} className="mono" required />
      </Field>
      <Field label="المنطقة الزمنية" hint="اسم IANA؛ لا تستخدم إزاحة ثابتة مثل +03:00">
        <input value={form.timezone} onChange={set('timezone')} className="mono" required />
      </Field>
      <Field label="العنوان (اختياري)">
        <input value={form.address} onChange={set('address')} />
      </Field>
      <Field label="جهة الاتصال (اختياري)">
        <input value={form.contact} onChange={set('contact')} />
      </Field>
    </Modal>
  );
}

function GroupForm({
  schools,
  busy,
  onCancel,
  onSubmit,
}: {
  schools: School[];
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: { name: string; priority: number; school_ids: string[] }) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [priority, setPriority] = useState(10);
  const [selected, setSelected] = useState<string[]>([]);

  return (
    <Modal
      title="إنشاء مجموعة مدارس"
      onClose={onCancel}
      footer={
        <>
          <button onClick={onCancel} disabled={busy}>
            إلغاء
          </button>
          <button
            className="primary"
            disabled={busy || !name}
            onClick={() => onSubmit({ name: name.trim(), priority, school_ids: selected })}
          >
            {busy ? 'جارٍ الحفظ…' : 'حفظ'}
          </button>
        </>
      }
    >
      <Field label="اسم المجموعة">
        <input value={name} onChange={(e) => setName(e.target.value)} required />
      </Field>
      <Field
        label="الأولوية"
        hint="الأعلى رقمًا يفوز. مجموعتان بالأولوية نفسها وقالبين مختلفين تمنعان النشر."
      >
        <input
          type="number"
          min={0}
          max={1000}
          value={priority}
          onChange={(e) => setPriority(Number(e.target.value))}
        />
      </Field>
      <Field label={`المدارس (${selected.length} محددة)`}>
        <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
          {schools.length === 0 ? (
            <div style={{ color: 'var(--muted)' }}>لا توجد مدارس بعد.</div>
          ) : (
            schools.map((s) => (
              <label key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text)' }}>
                <input
                  type="checkbox"
                  checked={selected.includes(s.id)}
                  onChange={(e) =>
                    setSelected((cur) =>
                      e.target.checked ? [...cur, s.id] : cur.filter((x) => x !== s.id),
                    )
                  }
                />
                {s.name} <span className="mono" style={{ color: 'var(--muted)' }}>({s.code})</span>
              </label>
            ))
          )}
        </div>
      </Field>
    </Modal>
  );
}
